/**
 * Stok Düzeltme Onayları — Sprint 36 (ADR-019).
 *
 * Eşik üstü stok düzeltmesi / fire kaydı yönetici onayı bekler.
 * "Sistem 10 kg gösteriyor ama 1 kg var" gibi büyük düzeltmeler
 * sahip görmeden commit edilmez.
 */
import {
  Badge, Button, Card, Group, Loader, Modal, SegmentedControl,
  Stack, Table, Text, TextInput,
} from "@mantine/core";
import {
  IconAlertTriangle, IconCheck, IconClock, IconX,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import {
  useAdjustmentRequests, useApproveAdjustment, useRejectAdjustment,
} from "../../../shared/api";
import { MoneyDisplay, PageHeader } from "../../../shared/ui";

const STATUS_COLOR: Record<string, string> = {
  PENDING: "orange",
  APPROVED: "green",
  REJECTED: "red",
  AUTO_APPROVED: "gray",
};
const STATUS_LABEL: Record<string, string> = {
  PENDING: "Onay Bekliyor",
  APPROVED: "Onaylandı",
  REJECTED: "Reddedildi",
  AUTO_APPROVED: "Otomatik Onay",
};
const KIND_LABEL: Record<string, string> = {
  ADJUSTMENT: "Düzeltme",
  WASTE: "Fire",
};

export function AdjustmentApprovalsPage() {
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const { data, isLoading } = useAdjustmentRequests(
    statusFilter === "ALL" ? {} : { status: statusFilter },
  );
  const approve = useApproveAdjustment();
  const reject = useRejectAdjustment();

  const [rejectTarget, setRejectTarget] = useState<any | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectOpened, { open: openReject, close: closeReject }] = useDisclosure(false);

  const requests = data?.results || data || [];
  const pendingCount = requests.filter((r: any) => r.status === "PENDING").length;

  const handleApprove = async (req: any) => {
    try {
      await approve.mutateAsync(req.id);
      notifications.show({
        title: "Onaylandı",
        message: `${req.ingredient_name} düzeltmesi ledger'a işlendi`,
        color: "green",
        icon: <IconCheck />,
      });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  };

  const openRejectModal = (req: any) => {
    setRejectTarget(req);
    setRejectReason("");
    openReject();
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try {
      await reject.mutateAsync({ id: rejectTarget.id, reason: rejectReason });
      notifications.show({
        title: "Reddedildi",
        message: "Düzeltme iptal edildi — stok hareketi yaratılmadı",
        color: "gray",
      });
      closeReject();
      setRejectTarget(null);
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="🔐 Stok Düzeltme Onayları"
        subtitle="Eşik üstü düzeltme ve fire kayıtları yönetici onayı bekler"
        actions={
          pendingCount > 0 ? (
            <Badge size="lg" color="orange" leftSection={<IconClock size={14} />}>
              {pendingCount} bekleyen
            </Badge>
          ) : null
        }
      />

      <SegmentedControl
        value={statusFilter}
        onChange={setStatusFilter}
        data={[
          { label: "Bekleyen", value: "PENDING" },
          { label: "Onaylanan", value: "APPROVED" },
          { label: "Reddedilen", value: "REJECTED" },
          { label: "Tümü", value: "ALL" },
        ]}
      />

      {isLoading ? <Loader /> : requests.length === 0 ? (
        <Card padding="xl" radius="md" withBorder>
          <Text c="dimmed" ta="center">
            {statusFilter === "PENDING"
              ? "Bekleyen onay yok 🎉"
              : "Kayıt bulunamadı"}
          </Text>
        </Card>
      ) : (
        <Table.ScrollContainer minWidth={720}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Tip</Table.Th>
              <Table.Th>Hammadde</Table.Th>
              <Table.Th>Lokasyon</Table.Th>
              <Table.Th ta="right">Miktar</Table.Th>
              <Table.Th ta="right">Değer</Table.Th>
              <Table.Th>İsteyen</Table.Th>
              <Table.Th>Durum</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {requests.map((req: any) => (
              <Table.Tr key={req.id}>
                <Table.Td>
                  <Badge variant="light" color={req.kind === "WASTE" ? "red" : "blue"}>
                    {KIND_LABEL[req.kind] || req.kind}
                  </Badge>
                </Table.Td>
                <Table.Td fw={500}>{req.ingredient_name}</Table.Td>
                <Table.Td>{req.location_name}</Table.Td>
                <Table.Td ta="right" fw={600}
                  {...(Number(req.delta) < 0 ? { c: "red" } : {})}>
                  {req.delta} {req.ingredient_unit}
                </Table.Td>
                <Table.Td ta="right">
                  <MoneyDisplay minor={req.value_minor_at_request} />
                </Table.Td>
                <Table.Td>{req.requested_by_username || "-"}</Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLOR[req.status]}>
                    {STATUS_LABEL[req.status] || req.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {req.status === "PENDING" ? (
                    <Group gap="xs">
                      <Button
                        size="xs"
                        color="green"
                        leftSection={<IconCheck size={14} />}
                        onClick={() => handleApprove(req)}
                        loading={approve.isPending}
                      >
                        Onayla
                      </Button>
                      <Button
                        size="xs"
                        color="red"
                        variant="light"
                        leftSection={<IconX size={14} />}
                        onClick={() => openRejectModal(req)}
                      >
                        Reddet
                      </Button>
                    </Group>
                  ) : req.status === "REJECTED" && req.rejection_reason ? (
                    <Text size="xs" c="dimmed">"{req.rejection_reason}"</Text>
                  ) : null}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </Table.ScrollContainer>
      )}

      <Modal
        opened={rejectOpened}
        onClose={closeReject}
        title="Düzeltmeyi Reddet"
      >
        <Stack>
          <Group gap="xs">
            <IconAlertTriangle color="orange" size={20} />
            <Text size="sm">
              {rejectTarget?.ingredient_name} — {rejectTarget?.delta}{" "}
              {rejectTarget?.ingredient_unit}
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            Reddedilirse stok hareketi <strong>yaratılmaz</strong>, stok
            değişmez. İsteyene sebep gösterilir.
          </Text>
          <TextInput
            label="Red sebebi"
            placeholder="Sayım tekrar yapılsın, kanıt yetersiz..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeReject}>Vazgeç</Button>
            <Button color="red" onClick={handleReject} loading={reject.isPending}>
              Reddet
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
